// @ts-nocheck
import { useEffect, useState } from 'react';
import { MdKeyboardBackspace } from 'react-icons/md';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  Alert,
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Textarea,
  Tooltip,
  useColorMode,
  useDisclosure,
  useToast,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  VStack,
  Table,
  Tbody,
  Tr,
  Td,
  Tag,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getAllActiveHttpsProviders,
  updateHttpsProvider,
  updateOverseer,
} from '../../redux/httpsProviders/actions';
import { pxToRem } from '../../utils';
import countryCodes from '../../utils/data/country-codes.json';
import { getApiUrl, getFirebaseIdToken, isAdmin } from '../../utils/utils';
import { getUserType } from '../../redux/user/selectors';
import { editProvider } from '../../utils/provider-builder/register-provider-utils';

export const ProviderEditScreen = () => {
  const { id: providerId } = useParams();

  const userType = useAppSelector(getUserType);
  const isUserAdmin = isAdmin(userType);

  const [provider, setProvider] = useState<any>([]);
  const [formData, setFormData] = useState({
    name,
    responseSelections: [],
    userAgent: {},
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newResponseSelectionData, setNewResponseSelectionData] = useState({
    jsonPath: '',
    responseMatch: '',
    xPath: '',
    matchType: 'greedy',
  });
  const [newResponseSelectionValidationError, setNewSelectionValidationError] = useState({
    jsonPath: false,
    responseMatch: false,
    xPath: false,
    matchType: false,
  });
  const [availableOverseers, setAvailableOverseers] = useState([]);
  const [selectedOverseer, setSelectedOverseer] = useState({
    overseerUid: '',
    overseerNote: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { colorMode } = useColorMode();
  const toast = useToast();
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchProvider = async () => {
    try {
      setLoading(true);
      const token = await getFirebaseIdToken();
      const { data } = await axios.get(`${getApiUrl()}/api/providers/${providerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data?.isSuccess) {
        setLoading(false);
        setProvider(data?.providers);
        setFormData(data?.providers);
        if (data?.providers?.overseerUid) {
          setSelectedOverseer({
            overseerUid: data?.providers?.overseerUid,
            overseerNote: data?.providers?.overseerNote,
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description:
          'Something went wrong while fetching provider. Please try again later.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProvider();
  }, []);

  useEffect(() => {
    const fetchOverseers = async () => {
      try {
        const token = await getFirebaseIdToken();
        const { data } = await axios.get(`${getApiUrl()}/api/users/admins`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (data?.isSuccess) {
          const overseers = data?.data?.map((user: any) => ({
            uid: user?.uid,
            name: user?.name,
          }));
          setAvailableOverseers(overseers);
        }
      } catch (error) {
        console.log(error);
        toast({
          title: 'Error',
          description:
            'Something went wrong while fetching overseer. Please try again later.',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (isUserAdmin) {
      fetchOverseers();
    }
  }, [isUserAdmin]);

  if (loading) {
    return (
      <Center p={4}>
        {' '}
        <Spinner /> Loading...
      </Center>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('userAgent.')) {
      // If the field belongs to userAgent, update it in a nested manner
      const userAgentField = name.split('.')[1]; // Extract the userAgent field name
      setFormData((prevData) => ({
        ...prevData,
        userAgent: {
          ...prevData.userAgent,
          [userAgentField]: value, // Update the specific userAgent field
        },
      }));
    } else {
      // For other fields, update normally
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleResponseSelectionChange = (index: number, field: string, value: any) => {
    const updatedResponseSelections = formData.responseSelections.map((selection, i) => {
      if (i !== index) {
        return selection;
      } // Keep unchanged selections as they are

      return {
        ...selection, // Keep other properties unchanged
        [field]: value, // Update only the specified field
      };
    });

    // Update the state with the modified response selections array and userAgent
    setFormData((prevState) => ({
      ...prevState,
      responseSelections: updatedResponseSelections,
    }));
  };

  const dispatch = useAppDispatch();

  const submitHandler = async () => {
    try {
      setIsSubmitting(true);
      let dataToSubmit = { ...formData };

      const token = (await getFirebaseIdToken()) ?? '';

      if (!isUserAdmin && formData.customInjection !== provider.customInjection) {
        dataToSubmit = {
          ...dataToSubmit,
          isApproved: !formData.customInjection?.length,
        };
      }

      const data = await editProvider({
        providerDetails: dataToSubmit,
        selectedNetworkRequest: null,
        accessToken: token,
        skipTransform: true,
      });

      setIsSubmitting(false);
      dispatch(getAllActiveHttpsProviders());
      if (data.isSuccess) {
        // check if overseer is changed and if overseer email exists, overseer not should not be empty
        if (selectedOverseer?.overseerUid?.length > 0 && isUserAdmin) {
          const res = await dispatch(
            updateOverseer({
              providerId,
              overseerUid: selectedOverseer.overseerUid,
              overseerNote: selectedOverseer.overseerNote || '',
            })
          );

          if (!res.payload?.isSuccess) {
            toast({
              title: 'Error Updating Overseer',
              description: 'Error Updating Overseer',
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }

        dispatch(getAllActiveHttpsProviders());
        toast({
          title: 'Provider Edited Successfully',
          description: 'Provider Edited Successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        if (!isUserAdmin) {
          navigate('/explore');
        } else {
          navigate('/admin/dashboard');
        }
      }
    } catch (error) {
      toast({
        title: 'Error Editing Provider',
        description: 'Error Editing Provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeNewResponseModal = () => {
    setNewResponseSelectionData({
      jsonPath: '',
      responseMatch: '',
      xPath: '',
      matchType: 'greedy',
    });
    onClose();
  };

  const handleNewResponseSelectionFieldChange = (e) => {
    setNewResponseSelectionData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (
      e.target.name === 'responseMatch' &&
      newResponseSelectionValidationError?.responseMatch
    ) {
      setNewSelectionValidationError((prev) => ({
        ...prev,
        responseMatch: false,
      }));
    }
  };

  const addNewResponseSelection = () => {
    if (!newResponseSelectionData?.responseMatch?.length) {
      setNewSelectionValidationError((prev) => ({
        ...prev,
        responseMatch: true,
      }));
      return;
    }

    const tempResponseSelections = [...formData.responseSelections];
    tempResponseSelections.push({
      ...newResponseSelectionData,
      invert: false,
    });
    setFormData((prevState) => ({
      ...prevState,
      responseSelections: tempResponseSelections,
    }));
    setProvider((prevState) => ({
      ...prevState,
      responseSelections: tempResponseSelections,
    }));
    closeNewResponseModal();
  };

  const handleRemoveResponseSelection = (index: number) => {
    const tempResponseSelections = [...formData.responseSelections];

    tempResponseSelections.splice(index, 1);

    setFormData((prevState) => ({
      ...prevState,
      responseSelections: tempResponseSelections,
    }));
    setProvider((prevState) => ({
      ...prevState,
      responseSelections: tempResponseSelections,
    }));
  };

  const handleOverseerDataChange = (e) => {
    setSelectedOverseer((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRequestDataChange = (
    index: number,
    field: string,
    idx: number,
    subField: string,
    value: string
  ) => {
    const updatedRequestData = [...formData.requestData];
    if (field === 'bodySniff.enabled') {
      updatedRequestData[index]['bodySniff']['enabled'] = value === 'true';
    } else if (field === 'bodySniff.template') {
      updatedRequestData[index]['bodySniff']['template'] = value;
    } else if (field === 'url') {
      updatedRequestData[index]['url'] = value;
    } else if (field === 'method') {
      updatedRequestData[index]['method'] = value;
    } else if (field === 'responseRedactions' && subField === 'hash') {
      updatedRequestData[index]['responseRedactions'][idx]['hash'] =
        value === 'null' ? null : value;
    } else {
      updatedRequestData[index][field][idx][subField] = value;
    }

    setFormData((prevState) => ({
      ...prevState,
      requestData: updatedRequestData,
    }));
  };

  const addNewRequestData = () => {
    const updatedRequestData = [...formData.requestData];
    updatedRequestData.push({
      url: '',
      urlType: 'TEMPLATE',
      method: 'GET',
      expectedPageUrl: '',
      responseMatches: [
        {
          description: '',
          invert: false,
          type: 'contains',
          value: '',
        },
      ],
      responseRedactions: [
        {
          hash: null,
          jsonPath: '',
          regex: '',
          xPath: '',
        },
      ],
      bodySniff: {
        enabled: false,
        regex: '',
        template: '',
      },
    });

    setFormData((prevState) => ({
      ...prevState,
      requestData: updatedRequestData,
    }));
  };

  const addNewData = (requestIndex: number) => {
    const updatedRequestData = [...formData.requestData];
    updatedRequestData[requestIndex]['responseMatches'].push({
      description: '',
      invert: false,
      type: 'contains',
      value: '',
    });
    updatedRequestData[requestIndex]['responseRedactions'].push({
      hash: null,
      jsonPath: '',
      regex: '',
      xPath: '',
    });
    setFormData((prevState) => ({
      ...prevState,
      requestData: updatedRequestData,
    }));
  };

  const removeData = (requestIndex: number, idx: number) => {
    const updatedRequestData = [...formData.requestData];
    updatedRequestData[requestIndex]['responseMatches'].splice(idx, 1);
    updatedRequestData[requestIndex]['responseRedactions'].splice(idx, 1);
    setFormData((prevState) => ({
      ...prevState,
      requestData: updatedRequestData,
    }));
  };

  const removeRequestData = (requestIndex: number) => {
    const updatedRequestData = [...formData.requestData];
    updatedRequestData.splice(requestIndex, 1);
    setFormData((prevState) => ({
      ...prevState,
      requestData: updatedRequestData,
    }));
  };

  return (
    <Box p={4}>
      <Box>
        <Box
          mt="32px"
          p="20px"
          borderRadius="lg"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="borderColor"
          bgColor={colorMode === 'dark' ? '' : 'white'}
        >
          <Flex gap={pxToRem(40)} mb={pxToRem(20)} alignItems="center">
            <IconButton
              variant="outline"
              colorScheme="blue"
              aria-label=""
              onClick={() => navigate(-1)}
              icon={<MdKeyboardBackspace />}
              width="fit-content"
            />
            <Flex w="full">
              <Alert
                borderRadius="lg"
                status="warning"
                w={'fit-content'}
                size="md"
                p={2}
                margin={'auto'}
              >
                <AlertIcon />
                Support for this version of the provider is deprecated. Please create a{' '}
                <span style={{ marginLeft: '4px', textDecoration: 'underline' }}>
                  <Link textDecoration="underline" to="/provider-builder">
                    new provider
                  </Link>
                </span>
                .
              </Alert>
            </Flex>
          </Flex>
          <Heading as="h2" size="xl" mb="20px">
            Provider Details
          </Heading>

          <Box p={4}>
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ marginBottom: '20px' }}>
                <FormControl flex="1" mr="2">
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData?.name}
                    onChange={handleChange}
                    placeholder={provider?.name}
                    required
                  />
                </FormControl>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <FormControl flex="1" ml="0">
                  <FormLabel htmlFor="description">Description:</FormLabel>
                  <Textarea
                    placeholder={provider?.description}
                    id="description"
                    value={formData?.description}
                    onChange={handleChange}
                    name="description"
                    resize="vertical"
                    required
                  />
                </FormControl>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginBottom: '20px',
                }}
              >
                {isUserAdmin && (
                  <FormControl flex="1" mr="2">
                    <FormLabel htmlFor="logoUrl">Logo URL:</FormLabel>
                    <Input
                      value={formData?.logoUrl}
                      placeholder={provider?.logoUrl}
                      onChange={handleChange}
                      type="text"
                      id="logoUrl"
                      name="logoUrl"
                    />
                  </FormControl>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  // marginBottom: '20px',
                }}
              >
                <FormControl flex="1" mr="2">
                  <FormLabel htmlFor="loginUrl">Login URL:</FormLabel>
                  <Input
                    value={formData?.loginUrl}
                    onChange={handleChange}
                    placeholder={provider?.loginUrl}
                    type="text"
                    id="loginUrl"
                    name="loginUrl"
                  />
                </FormControl>
              </div>

              <div />

              <HStack mb={pxToRem(20)}>
                <FormControl flex="1" mt="4">
                  <FormLabel htmlFor="method">Method:</FormLabel>
                  <Select
                    placeholder={formData.method}
                    onChange={handleChange}
                    name="method"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </Select>
                </FormControl>

                <FormControl flex="1" mt="4">
                  <FormLabel htmlFor="geoLocation">Geo Location:</FormLabel>
                  <Select
                    name="geoLocation"
                    placeholder={
                      provider?.geoLocation ? provider?.geoLocation : 'Select option'
                    }
                    value={formData.geoLocation}
                    onChange={handleChange}
                  >
                    {countryCodes.map((countryCode: { Code: string; Name: string }) => (
                      <option key={countryCode.Code} value={countryCode.Code}>
                        {countryCode.Name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <HStack mb={pxToRem(20)}>
                <FormControl flex="1" mt="0">
                  <FormLabel htmlFor="method">Provider Type:</FormLabel>
                  <Select
                    placeholder={formData.providerType}
                    onChange={handleChange}
                    name="providerType"
                  >
                    <option value="PUBLIC">PUBLIC</option>
                    <option value="PRIVATE">PRIVATE</option>
                  </Select>
                </FormControl>
              </HStack>

              <Box mb={pxToRem(20)}>
                <FormControl>
                  <FormLabel>Steps to follow</FormLabel>
                  <Textarea
                    value={formData?.stepsToFollow}
                    placeholder={formData?.stepsToFollow}
                    onChange={handleChange}
                    name="stepsToFollow"
                  />
                </FormControl>
              </Box>

              <div>
                <FormControl>
                  <FormLabel>Custom JS Injection</FormLabel>
                  <Textarea
                    value={formData?.customInjection}
                    placeholder={
                      formData?.customInjection
                        ? formData?.customInjection
                        : 'Add Custom Injection'
                    }
                    onChange={handleChange}
                    name="customInjection"
                  />
                </FormControl>
                {!isUserAdmin && (
                  <Alert borderRadius="lg" status="warning" mt={pxToRem(10)}>
                    <AlertIcon />
                    Providers using custom JavaScript injections must be approved by the
                    admin before they can be used.
                  </Alert>
                )}
              </div>
              <Alert
                borderRadius="lg"
                border={colorMode === 'dark' ? '1px solid #e5e5ea' : 'none'}
                status="warning"
                mt={4}
              >
                {' '}
                Editing the request data may break your provider, so please only edit it
                if you are sure about the request data.{' '}
              </Alert>

              <Box borderWidth="2px" borderRadius="lg" mt={6} position="relative">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={4}
                  px={6}
                  borderBottom="1px solid #e5e5ea"
                >
                  <Heading style={{ marginBottom: '0px', fontSize: '18px' }}>
                    Request Details
                  </Heading>
                  <Button colorScheme="blue" onClick={addNewRequestData}>
                    Add Request
                  </Button>
                </Box>
                <Tabs variant="line" mt={2}>
                  <TabList>
                    {formData?.requestData?.map((_, index) => (
                      <Tab key={index}>Request {index + 1}</Tab>
                    ))}
                  </TabList>
                  <TabPanels>
                    {formData?.requestData?.map((request, index) => (
                      <TabPanel key={index}>
                        <VStack align="stretch" spacing={4} w="full">
                          <Box borderWidth="1px" borderRadius="lg" p={4}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Text fontWeight="bold" mb={2}>
                                Request Details
                              </Text>
                              <IconButton
                                variant="outline"
                                colorScheme="red"
                                aria-label="Delete Request"
                                fontSize="20px"
                                icon={<DeleteIcon />}
                                onClick={() => removeRequestData(index)}
                              />
                            </div>
                            <FormControl mb={2}>
                              <FormLabel>URL</FormLabel>
                              <Input
                                value={request.url}
                                onChange={(e) =>
                                  handleRequestDataChange(
                                    index,
                                    'url',
                                    null,
                                    null,
                                    e.target.value
                                  )
                                }
                              />
                            </FormControl>
                            <FormControl mb={2}>
                              <FormLabel>Method</FormLabel>
                              <Select
                                value={request.method}
                                onChange={(e) =>
                                  handleRequestDataChange(
                                    index,
                                    'method',
                                    null,
                                    null,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                              </Select>
                            </FormControl>
                            <FormControl mb={2}>
                              <FormLabel>Request Body Enabled</FormLabel>
                              <Select
                                value={request.bodySniff.enabled ? 'true' : 'false'}
                                onChange={(e) =>
                                  handleRequestDataChange(
                                    index,
                                    'bodySniff.enabled',
                                    null,
                                    null,
                                    e.target.value
                                  )
                                }
                              >
                                <option value={true}>True</option>
                                <option value={false}>False</option>
                              </Select>
                            </FormControl>
                            <FormControl mb={2}>
                              <FormLabel>Request Body Value</FormLabel>
                              <Textarea
                                name="responseMatch"
                                value={request.bodySniff.template}
                                onChange={(e) =>
                                  handleRequestDataChange(
                                    index,
                                    'bodySniff.template',
                                    null,
                                    null,
                                    e.target.value
                                  )
                                }
                                placeholder="Body Sniff Template"
                              />
                            </FormControl>
                          </Box>

                          <Box borderWidth="1px" borderRadius="lg">
                            <Tabs variant="enclosed-colored">
                              <TabList>
                                {request.responseMatches.map((match, idx) => (
                                  <Tab key={`data-${idx + 1}`}>Data {idx + 1}</Tab>
                                ))}
                              </TabList>
                              {request.responseMatches.length === 0 ? (
                                <>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      gap: '20px',
                                      padding: '20px',
                                    }}
                                  >
                                    <Button
                                      colorScheme="blue"
                                      onClick={() => addNewData(index)}
                                    >
                                      Add new Data To Request
                                    </Button>
                                  </div>
                                </>
                              ) : (
                                <TabPanels>
                                  {new Array(request.responseMatches.length)
                                    .fill(0)
                                    .map((_, idx) => {
                                      const responseMatchData =
                                        request.responseMatches[idx];
                                      const responseRedactionData =
                                        request.responseRedactions[idx];
                                      return (
                                        <TabPanel>
                                          <FormControl mb={2}>
                                            <FormLabel>Response Match </FormLabel>
                                            <Input
                                              value={responseMatchData.value}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseMatches',
                                                  idx,
                                                  'value',
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl mb={pxToRem(20)}>
                                            <FormLabel>Type</FormLabel>
                                            <Select
                                              name="type"
                                              value={responseMatchData.type}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseMatches',
                                                  idx,
                                                  'type',
                                                  e.target.value
                                                )
                                              }
                                            >
                                              <option value="contains">Contains</option>
                                              <option value="regex">Regex</option>
                                            </Select>
                                          </FormControl>
                                          <FormControl mb={2}>
                                            <FormLabel>Description </FormLabel>
                                            <Input
                                              value={responseMatchData.description}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseMatches',
                                                  idx,
                                                  'description',
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl mb={2}>
                                            <FormLabel>JSON Path </FormLabel>
                                            <Input
                                              value={responseRedactionData.jsonPath}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseRedactions',
                                                  idx,
                                                  'jsonPath',
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl mb={2}>
                                            <FormLabel>X Path</FormLabel>
                                            <Input
                                              value={responseRedactionData.xPath}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseRedactions',
                                                  idx,
                                                  'xPath',
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl mb={2}>
                                            <FormLabel>Regex</FormLabel>
                                            <Input
                                              value={responseRedactionData.regex}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseRedactions',
                                                  idx,
                                                  'regex',
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl mb={pxToRem(20)}>
                                            <FormLabel>Hash</FormLabel>
                                            <Select
                                              name="hash"
                                              value={responseRedactionData.hash}
                                              onChange={(e) =>
                                                handleRequestDataChange(
                                                  index,
                                                  'responseRedactions',
                                                  idx,
                                                  'hash',
                                                  e.target.value
                                                )
                                              }
                                            >
                                              <option value={'null'}>Null</option>
                                              <option value="oprf">OPRF</option>
                                            </Select>
                                          </FormControl>
                                          <div
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'flex-end',
                                              gap: '20px',
                                            }}
                                          >
                                            <Button
                                              colorScheme="blue"
                                              onClick={() => addNewData(index)}
                                            >
                                              Add new Data
                                            </Button>
                                            <Button
                                              colorScheme="red"
                                              onClick={() => removeData(index, idx)}
                                            >
                                              Remove Data
                                            </Button>
                                          </div>
                                        </TabPanel>
                                      );
                                    })}
                                </TabPanels>
                              )}
                            </Tabs>
                          </Box>
                        </VStack>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              </Box>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginBottom: '20px',
                }}
              >
                <FormControl flex="1" mt="4">
                  <Box mb={pxToRem(20)}>
                    <FormLabel htmlFor="android">Android</FormLabel>
                    <Input
                      onChange={handleChange}
                      value={formData?.userAgent?.android}
                      placeholder={provider?.userAgent?.android || 'Add User Agent'}
                      id="android"
                      name="userAgent.android"
                    />
                  </Box>
                  <Box mb={pxToRem(10)}>
                    <FormLabel htmlFor="ios">iOS</FormLabel>
                    <Input
                      onChange={handleChange}
                      value={formData?.userAgent?.ios}
                      placeholder={provider?.userAgent?.ios || 'Add User Agent'}
                      id="ios"
                      name="userAgent.ios"
                    />
                  </Box>
                </FormControl>
              </div>
              {isUserAdmin && (
                <>
                  <HStack mb={pxToRem(20)}>
                    <FormControl flex="1" mt="0">
                      <FormLabel htmlFor="method">Provider Overseer:</FormLabel>
                      <Select
                        placeholder="Select Overseer"
                        onChange={handleOverseerDataChange}
                        value={selectedOverseer.overseerUid || ''}
                        name="overseerUid"
                      >
                        {availableOverseers.map((overseer: any, index: number) => (
                          <option
                            key={overseer.uid + 'overseer' + index}
                            value={overseer.uid}
                          >
                            {overseer.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </HStack>
                  <HStack mb={pxToRem(20)}>
                    <FormControl flex="1" ml="0">
                      <FormLabel htmlFor="description">Overseer Note:</FormLabel>
                      <Textarea
                        placeholder="Add a note to the overseer"
                        id="overseerNote"
                        value={selectedOverseer.overseerNote || ''}
                        onChange={handleOverseerDataChange}
                        name="overseerNote"
                        resize="vertical"
                      />
                    </FormControl>
                  </HStack>
                </>
              )}

              <Button
                mt={0}
                isLoading={isSubmitting}
                colorScheme="blue"
                onClick={submitHandler}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
